import { ReactNode, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom'
import { Activity,
  Menu, X, Wallet, LineChart, ArrowLeftRight, LayoutGrid, Coins, LogOut, User2, Repeat,
  ShieldCheck, KeyRound, Bell, Users, History, Settings2, ChevronLeft, Sparkles, Radio,
  Send, LifeBuoy, CreditCard, Store,
} from 'lucide-react'
import { clsx, num, fa, timeAgo, asArray} from '../lib/format'
import { useAuth, useMarket } from '../store'
import { get, post } from '../lib/api'
import { useSeo } from '../lib/seo'

const NAV = [
  { to: '/markets', label: 'بازارها', icon: LayoutGrid },
  { to: '/pulse', label: 'نبض بازار', icon: Activity },
  { to: '/trade/BTCIRT', label: 'معامله حرفه‌ای', icon: LineChart },
  { to: '/convert', label: 'خرید سریع و تبدیل', icon: ArrowLeftRight },
  { to: '/staking', label: 'استیکینگ', icon: Coins },
  { to: '/blog', label: 'آموزش و اخبار', icon: Sparkles },
  { to: '/merchants', label: 'درگاه پذیرندگی', icon: Store },
]

export const PANEL_NAV = [
  { to: '/dashboard', label: 'داشبورد', icon: LayoutGrid },
  { to: '/wallet', label: 'کیف پول', icon: Wallet },
  { to: '/crypto', label: 'واریز و برداشت رمزارز', icon: Repeat },
  { to: '/cards', label: 'کارت‌های بانکی', icon: CreditCard },
  { to: '/transfer', label: 'انتقال داخلی', icon: Send },
  { to: '/history', label: 'سفارش‌ها و تراکنش‌ها', icon: History },
  { to: '/kyc', label: 'احراز هویت', icon: ShieldCheck },
  { to: '/alerts', label: 'هشدار قیمت', icon: Bell },
  { to: '/referral', label: 'دعوت دوستان', icon: Users },
  { to: '/api-keys', label: 'کلید API', icon: KeyRound },
  { to: '/merchant', label: 'درگاه پذیرندگی', icon: Store },
  { to: '/support', label: 'پشتیبانی', icon: LifeBuoy },
  { to: '/security', label: 'امنیت و تنظیمات', icon: Settings2 },
]

export function Logo({ size = 'md' }: { size?: 'sm' | 'md' }) {
  return (
    <Link to="/" className="flex items-center gap-2.5">
      <span
        className={clsx(
          'grid place-items-center rounded-xl bg-gradient-to-br from-brand-500 to-aqua font-black text-white shadow-glow',
          size === 'sm' ? 'h-8 w-8 text-sm' : 'h-10 w-10 text-lg',
        )}
      >
        A
      </span>
      <span className={clsx('font-extrabold tracking-tight text-white', size === 'sm' ? 'text-base' : 'text-lg')}>
        ارزینو
      </span>
    </Link>
  )
}

function LiveDot() {
  const { connected, source } = useMarket()
  return (
    <span className="chip !py-0.5 text-[11px] text-slate-400">
      <Radio className={clsx('h-3 w-3', connected ? 'animate-pulseSoft text-up' : 'text-slate-600')} />
      {connected ? (source === 'simulator' ? 'داده شبیه‌سازی' : 'قیمت زنده') : 'در حال اتصال'}
    </span>
  )
}

function NotificationBell() {
  const { user } = useAuth()
  const [open, setOpen] = useState(false)
  const [data, setData] = useState<any>({ unread: 0, data: [] })

  // پاسخ باید شکل {unread, data} داشته باشد؛ هر شکل دیگری به همان قالب
  // نگاشت می‌شود تا رندر بعدی روی مقدار غیرمنتظره نشکند.
  const load = () => {
    if (!user) return
    get('/notifications?limit=20')
      .then((r: any) => setData({ unread: Number(r?.unread) || 0, data: asArray(r?.data ?? r) }))
      .catch(() => {})
  }
  useEffect(() => {
    load()
    const id = setInterval(load, 45000)
    return () => clearInterval(id)
  }, [user])

  if (!user) return null
  const KIND: Record<string, string> = {
    success: 'border-up/30 text-up', danger: 'border-down/30 text-down',
    warning: 'border-gold/30 text-gold', info: 'border-brand-500/30 text-brand-200',
  }

  return (
    <div className="relative">
      <button onClick={() => {
        setOpen((o) => !o)
        if (!open && data.unread) post('/notifications/read').then(load).catch(() => {})
      }} className="btn-ghost relative !px-2.5">
        <Bell className="h-4 w-4" />
        {data.unread > 0 && (
          <span className="num absolute -left-1 -top-1 grid h-4 min-w-[1rem] place-items-center rounded-full bg-down px-1 text-[9px] font-bold text-white">
            {fa(data.unread)}
          </span>
        )}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute left-0 z-20 mt-2 max-h-[26rem] w-80 overflow-y-auto rounded-2xl border border-white/10 bg-ink-900/97 p-2 shadow-card backdrop-blur-xl">
            <div className="px-2 py-2 text-xs font-bold text-white">اعلان‌ها</div>
            {data.data.length === 0 && (
              <p className="py-8 text-center text-xs text-slate-500">اعلانی ندارید</p>
            )}
            {data.data.map((n: any) => {
              // اعلانی که مقصد دارد (مثلاً پاسخ تیکت) باید کاربر را همان‌جا
              // ببرد؛ وگرنه کاربر خبر را می‌بیند و باید خودش دنبال صفحه بگردد.
              const body = (
                <>
                  <div className="mb-1 text-xs font-semibold">{n.title}</div>
                  {n.body && <p className="text-[11px] leading-6 text-slate-400">{n.body}</p>}
                  <div className="mt-1 text-[10px] text-slate-600">{timeAgo(n.createdAt)}</div>
                </>
              )
              const cls = clsx('mb-1 block w-full rounded-xl border bg-white/[.03] p-3 text-right',
                KIND[n.kind] ?? 'border-white/10 text-slate-300')
              return n.link ? (
                <Link key={n.id} to={n.link} onClick={() => setOpen(false)}
                      className={clsx(cls, 'transition hover:bg-white/[.06]')}>
                  {body}
                </Link>
              ) : (
                <div key={n.id} className={cls}>{body}</div>
              )
            })}
          </div>
        </>
      )}
    </div>
  )
}

function UserMenu() {
  const { user, logout } = useAuth()
  const [open, setOpen] = useState(false)
  const nav = useNavigate()
  if (!user) return null
  return (
    <div className="relative">
      <button onClick={() => setOpen((o) => !o)} className="btn-ghost !px-3">
        <User2 className="h-4 w-4" />
        <span className="hidden max-w-[8rem] truncate sm:inline">{user.fullName || (user.email ?? '').split('@')[0]}</span>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute left-0 z-20 mt-2 w-60 rounded-2xl border border-white/10 bg-ink-900/95 p-2 shadow-card backdrop-blur-xl">
            <div className="border-b border-white/[.06] px-3 pb-3 pt-2">
              <div className="truncate text-sm font-semibold text-white">{user.email}</div>
              <div className="mt-1 text-xs text-slate-500">
                سطح احراز هویت: <span className="text-brand-200">{fa(user.kycLevel)}</span>
              </div>
            </div>
            {PANEL_NAV.map((i) => (
              <button
                key={i.to}
                onClick={() => { setOpen(false); nav(i.to) }}
                className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-slate-300 transition hover:bg-white/[.06] hover:text-white"
              >
                <i.icon className="h-4 w-4 text-slate-500" />
                {i.label}
              </button>
            ))}
            {user.isAdmin && (
              <button
                onClick={() => { setOpen(false); nav('/admin') }}
                className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-gold transition hover:bg-white/[.06]"
              >
                <ShieldCheck className="h-4 w-4" /> پنل مدیریت
              </button>
            )}
            <button
              onClick={() => { logout(); setOpen(false); nav('/') }}
              className="mt-1 flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-down transition hover:bg-down/10"
            >
              <LogOut className="h-4 w-4" /> خروج از حساب
            </button>
          </div>
        </>
      )}
    </div>
  )
}

export function Navbar() {
  const { user } = useAuth()
  const usdt = useMarket((s) => s.tickers['USDTIRT'])
  const [open, setOpen] = useState(false)
  const [solid, setSolid] = useState(false)
  const loc = useLocation()

  useEffect(() => setOpen(false), [loc.pathname])
  useEffect(() => {
    const fn = () => setSolid(window.scrollY > 12)
    window.addEventListener('scroll', fn)
    return () => window.removeEventListener('scroll', fn)
  }, [])

  return (
    <header
      className={clsx(
        // `pt-safe` فضای ناچ را جبران می‌کند و `app-header` در حالت
        // نصب‌شده پس‌زمینه می‌دهد؛ هر دو در مرورگر معمولی بی‌اثرند.
        'app-header pt-safe sticky top-0 z-50 transition-all duration-300',
        solid ? 'border-b border-white/[.07] bg-ink-950/85 backdrop-blur-2xl' : 'bg-transparent',
      )}
    >
      <div className="mx-auto flex h-16 max-w-[1400px] items-center gap-4 px-4 sm:px-6">
        <Logo />
        <nav className="hidden items-center gap-1 lg:flex">
          {NAV.map((i) => (
            <NavLink
              key={i.to}
              to={i.to}
              className={({ isActive }) =>
                clsx(
                  'rounded-xl px-3.5 py-2 text-sm font-medium transition',
                  isActive ? 'bg-white/[.07] text-white' : 'text-slate-400 hover:bg-white/[.05] hover:text-slate-100',
                )
              }
            >
              {i.label}
            </NavLink>
          ))}
        </nav>

        <div className="mr-auto flex items-center gap-2">
          {usdt && (
            <span className="chip hidden !py-1 text-[11px] md:inline-flex">
              <span className="text-slate-500">تتر</span>
              <span className="num font-semibold text-up">{num(usdt.last)}</span>
              <span className="text-slate-500">تومان</span>
            </span>
          )}
          <span className="hidden md:inline-flex"><LiveDot /></span>
          {user ? (
            <>
              <NotificationBell />
              <Link to="/wallet" className="btn-ghost hidden !px-3 sm:inline-flex">
                <Wallet className="h-4 w-4" />
                <span className="hidden lg:inline">کیف پول</span>
              </Link>
              <UserMenu />
            </>
          ) : (
            <div className="flex items-center gap-2">
              <Link to="/login" className="btn-ghost !px-3 text-xs sm:text-sm">ورود</Link>
              <Link to="/register" className="btn-primary !px-3 text-xs sm:!px-4 sm:text-sm">ثبت‌نام رایگان</Link>
            </div>
          )}
          <button className="btn-ghost !px-2.5 lg:hidden" onClick={() => setOpen((o) => !o)}>
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-white/[.07] bg-ink-950/97 px-4 py-3 backdrop-blur-2xl lg:hidden">
          {NAV.map((i) => (
            <NavLink
              key={i.to}
              to={i.to}
              className="flex items-center justify-between rounded-xl px-3 py-3 text-sm text-slate-300 transition hover:bg-white/[.05]"
            >
              <span className="flex items-center gap-2.5"><i.icon className="h-4 w-4 text-slate-500" />{i.label}</span>
              <ChevronLeft className="h-4 w-4 text-slate-600" />
            </NavLink>
          ))}
        </div>
      )}
    </header>
  )
}

/**
 * ناوبری موبایل
 * ==============
 * نسخه قبلی چهار میان‌بر داشت و بس: خانه، بازار، تبدیل، کیف پول. هر بخش
 * دیگری — احراز هویت، کارت بانکی، تاریخچه، پشتیبانی — فقط از راه منوی کاربر
 * در گوشه بالای صفحه در دسترس بود؛ یعنی درست دورترین نقطه از شست کاربر.
 *
 * حالا دکمه پنجم «همه» یک کشوی کامل باز می‌کند که **هر بخش سایت** در آن یک
 * لمس فاصله دارد، و خودِ نوار تا زیر نوار خانه‌ی آیفون کشیده می‌شود
 * (`safe-area-inset`) تا دکمه‌ها زیر آن گم نشوند.
 */
function SheetRow({ to, label, icon: Icon, onGo, tone }: any) {
  return (
    <button
      onClick={() => onGo(to)}
      className={clsx(
        'flex w-full items-center gap-3 rounded-xl px-3 py-3.5 text-right text-sm transition active:bg-white/[.08]',
        tone === 'danger' ? 'text-down' : tone === 'gold' ? 'text-gold' : 'text-slate-200',
      )}
    >
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl border border-white/[.08] bg-white/[.04]">
        <Icon className="h-4 w-4" />
      </span>
      <span className="flex-1">{label}</span>
      <ChevronLeft className="h-4 w-4 text-slate-600" />
    </button>
  )
}

function MobileMenuSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { user, logout } = useAuth()
  const nav = useNavigate()

  // قفل اسکرول پس‌زمینه تا کشو باز است؛ وگرنه انگشت کاربر صفحه پشتی را
  // می‌لغزاند و کشو سرِ جایش می‌ماند.
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : ''
    return () => { document.body.style.overflow = '' }
  }, [open])

  if (!open) return null

  const go = (to: string) => { onClose(); nav(to) }
  const INFO = [
    { to: '/blog', label: 'آموزش و اخبار', icon: Sparkles },
    { to: '/faq', label: 'سؤالات متداول', icon: LifeBuoy },
    { to: '/fees', label: 'کارمزدها', icon: Coins },
    { to: '/about', label: 'درباره ارزینو', icon: Radio },
  ]

  return createPortal(
    <div className="fixed inset-0 z-[60] lg:hidden">
      <div className="absolute inset-0 bg-ink-950/80 backdrop-blur-sm" onClick={onClose} />
      <div
        className="absolute inset-x-0 bottom-0 max-h-[88vh] overflow-y-auto rounded-t-3xl border-t border-white/10 bg-ink-900/98 backdrop-blur-2xl"
        style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom, 0px))' }}
      >
        {/* دستگیره کشو — نشانه‌ای که به کاربر می‌گوید این پنجره کشیدنی است */}
        <div className="sticky top-0 z-10 flex items-center justify-between gap-2 border-b border-white/[.06] bg-ink-900/98 px-4 pb-3 pt-3">
          <div className="absolute inset-x-0 top-1.5 mx-auto h-1 w-10 rounded-full bg-white/15" />
          <span className="mt-2 text-sm font-bold text-white">همه بخش‌ها</span>
          <button onClick={onClose} className="tap mt-2 rounded-lg p-1.5 text-slate-400 active:bg-white/10">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-2">
          {user ? (
            <>
              <div className="px-3 pb-1 pt-2 text-[11px] font-bold text-slate-500">حساب من</div>
              {PANEL_NAV.map((i) => <SheetRow key={i.to} {...i} onGo={go} />)}
              {user.isAdmin && (
                <SheetRow to="/admin" label="پنل مدیریت" icon={ShieldCheck} onGo={go} tone="gold" />
              )}
            </>
          ) : (
            <div className="grid grid-cols-2 gap-2 p-2">
              <button onClick={() => go('/login')} className="btn-ghost !py-3">ورود</button>
              <button onClick={() => go('/register')} className="btn-primary !py-3">ثبت‌نام</button>
            </div>
          )}

          <div className="px-3 pb-1 pt-3 text-[11px] font-bold text-slate-500">بازار و معامله</div>
          {NAV.map((i) => <SheetRow key={i.to} {...i} onGo={go} />)}

          <div className="px-3 pb-1 pt-3 text-[11px] font-bold text-slate-500">راهنما</div>
          {INFO.map((i) => <SheetRow key={i.to} {...i} onGo={go} />)}

          {user && (
            <>
              <div className="my-2 h-px bg-white/[.06]" />
              <SheetRow to="/" label="خروج از حساب" icon={LogOut} tone="danger"
                onGo={() => { logout(); onClose(); nav('/') }} />
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  )
}

export function MobileTabBar() {
  const { user } = useAuth()
  const [menu, setMenu] = useState(false)
  const loc = useLocation()

  useEffect(() => setMenu(false), [loc.pathname])

  const items = [
    { to: '/', label: 'خانه', icon: LayoutGrid, end: true },
    { to: '/markets', label: 'بازار', icon: LineChart },
    { to: '/convert', label: 'تبدیل', icon: ArrowLeftRight },
    { to: user ? '/wallet' : '/login', label: user ? 'کیف پول' : 'ورود', icon: Wallet },
  ]

  return (
    <>
      <nav
        className="fixed bottom-0 left-0 right-0 z-40 border-t border-white/[.08] bg-ink-950/95 backdrop-blur-2xl lg:hidden"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        <div className="flex">
          {items.map((i) => (
            <NavLink
              key={i.to}
              to={i.to}
              end={i.end}
              className={({ isActive }) =>
                clsx('flex min-h-[3.25rem] flex-1 flex-col items-center justify-center gap-1 text-[10px] transition active:bg-white/[.06]',
                  isActive && !menu ? 'text-brand-200' : 'text-slate-500')
              }
            >
              <i.icon className="h-5 w-5" />
              {i.label}
            </NavLink>
          ))}
          <button
            onClick={() => setMenu((m) => !m)}
            className={clsx('flex min-h-[3.25rem] flex-1 flex-col items-center justify-center gap-1 text-[10px] transition active:bg-white/[.06]',
              menu ? 'text-brand-200' : 'text-slate-500')}
          >
            <Menu className="h-5 w-5" />
            همه
          </button>
        </div>
      </nav>
      <MobileMenuSheet open={menu} onClose={() => setMenu(false)} />
    </>
  )
}

export function Footer() {
  const cols = [
    // «حریم خصوصی» عمداً بلافاصله زیر «درباره ما» است و نه ته فهرست:
    // کافه‌بازار و فروشگاه‌های دیگر انتظار دارند این صفحه از سایت به‌راحتی
    // در دسترس باشد، و کاربری هم که دنبالش می‌گردد معمولاً همان‌جا را
    // نگاه می‌کند.
    { title: 'ارزینو', links: [['درباره ما', '/about'], ['سیاست حریم خصوصی', '/privacy'], ['کارمزدها', '/fees'], ['سؤالات متداول', '/faq'], ['وبلاگ', '/blog'], ['اپلیکیشن اندروید', '/app']] },
    { title: 'خدمات', links: [['بازارها', '/markets'], ['نبض بازار', '/pulse'], ['معامله حرفه‌ای', '/trade/BTCIRT'], ['تبدیل ارز', '/convert'], ['استیکینگ', '/staking'], ['درگاه پذیرندگی', '/merchants']] },
    { title: 'کاربران', links: [['ثبت‌نام', '/register'], ['ورود', '/login'], ['دعوت دوستان', '/referral'], ['پشتیبانی', '/support'], ['کلید API', '/api-keys']] },
  ]
  return (
    <footer className="pb-tabbar mt-20 border-t border-white/[.07] bg-ink-950/60 pt-14 lg:pb-14">
      <div className="mx-auto max-w-[1400px] px-4 sm:px-6">
        <div className="grid gap-10 md:grid-cols-[1.4fr_repeat(3,1fr)]">
          <div>
            <Logo />
            <p className="mt-4 max-w-sm text-sm leading-7 text-slate-400">
              ارزینو بستری امن برای خرید، فروش و نگهداری ارزهای دیجیتال است؛ با کمترین کارمزد،
              تسویه آنی ریالی و پشتیبانی شبانه‌روزی.
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              <span className="chip text-[11px]">نماد اعتماد الکترونیکی</span>
              <span className="chip text-[11px]">کیف پول سرد چندامضایی</span>
              <span className="chip text-[11px]">پشتیبانی ۲۴/۷</span>
            </div>
          </div>
          {cols.map((c) => (
            <div key={c.title}>
              <h4 className="mb-4 font-bold text-white">{c.title}</h4>
              <ul className="space-y-2.5 text-sm">
                {c.links.map(([label, to]) => (
                  <li key={to}><Link to={to} className="link-muted">{label}</Link></li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="divider my-8" />
        <div className="flex flex-col items-center justify-between gap-3 text-xs text-slate-500 sm:flex-row">
          <p>© {fa(new Date().getFullYear())} ارزینو — تمامی حقوق محفوظ است.</p>
          <p>سرمایه‌گذاری در بازار رمزارز با ریسک همراه است؛ مسئولیت تصمیم‌ها بر عهده کاربر است.</p>
        </div>
      </div>
    </footer>
  )
}

function AnnouncementBar() {
  const [rows, setRows] = useState<any[]>([])
  const [hidden, setHidden] = useState<number[]>([])
  useEffect(() => { get('/announcements').then((r: any) => setRows(asArray(r))).catch(() => {}) }, [])
  const visible = rows.filter((r) => r.placement === 'banner' && !hidden.includes(r.id))
  if (!visible.length) return null
  const KIND: Record<string, string> = {
    success: 'border-up/25 bg-up/[.07] text-up',
    danger: 'border-down/25 bg-down/[.07] text-down',
    warning: 'border-gold/25 bg-gold/[.07] text-gold',
    info: 'border-brand-500/25 bg-brand-500/[.07] text-brand-200',
  }
  return (
    <div className="mx-auto w-full max-w-[1400px] px-4 pt-4 sm:px-6">
      {visible.map((a) => (
        <div key={a.id} className={clsx('mb-2 flex items-start gap-3 rounded-2xl border p-3.5 text-xs leading-6',
          KIND[a.kind] ?? KIND.info)}>
          <div className="min-w-0 flex-1">
            <b>{a.title}</b>
            {a.body && <span className="mr-2 opacity-90">{a.body}</span>}
          </div>
          <button onClick={() => setHidden((h) => [...h, a.id])} className="shrink-0 opacity-70 hover:opacity-100">
            <X className="h-4 w-4" />
          </button>
        </div>
      ))}
    </div>
  )
}

export function Shell({ children, full }: { children: ReactNode; full?: boolean }) {
  return (
    <div className="flex min-h-full flex-col">
      <Navbar />
      <AnnouncementBar />
      <main className={clsx('flex-1', full ? '' : 'mx-auto w-full max-w-[1400px] px-3 py-5 sm:px-6 sm:py-8')}>{children}</main>
      <Footer />
      <MobileTabBar />
    </div>
  )
}

export function PanelShell({ children, title, subtitle }: { children: ReactNode; title: string; subtitle?: string }) {
  // هیچ صفحه‌ای از پنل کاربر نباید ایندکس شود. robots.txt هم همین را می‌گوید،
  // ولی robots فقط «نخز» است نه «ایندکس نکن»؛ اگر جایی به این صفحه لینک داده
  // شود، گوگل می‌تواند بدون خواندنش آن را در نتایج بیاورد. این تگ جلوی آن را
  // می‌گیرد.
  useSeo({ title, description: subtitle, noindex: true }, [title, subtitle])
  return (
    <Shell>
      {/*
        چرا `min-w-0` و `minmax(0,1fr)`؟

        فرزند یک گرید (یا فلکس) به‌طور پیش‌فرض `min-width: auto` دارد؛ یعنی
        کمینه عرضش برابر **کم‌عرض‌ترین حالتِ محتوایش** است، نه صفر. کافی است
        یک چیز پهن جایی درون آن باشد — اینجا ردیفِ افقیِ بخش‌های پنل — تا کل
        ستون به اندازه آن باز شود.

        اندازه‌گیری روی خود سایت: بدون این، `<section>` در صفحه ۳۸۶ پیکسلی
        **۱۴۳۵ پیکسل** عرض می‌گرفت و همه کارت‌ها با آن از کادر بیرون می‌زدند.
        با `min-w-0` همان بخش ۳۵۳ پیکسل می‌شود.

        این یک خط، هر صفحه پنل را با هم درست می‌کند: داشبورد، کیف پول،
        کارت‌ها، احراز هویت، تاریخچه، پشتیبانی و بقیه.
      */}
      <div className="grid gap-4 lg:grid-cols-[260px_minmax(0,1fr)] lg:gap-6">
        <aside className="hidden lg:block">
          <div className="card sticky top-24 p-3">
            {PANEL_NAV.map((i) => (
              <NavLink
                key={i.to}
                to={i.to}
                className={({ isActive }) =>
                  clsx('mb-1 flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition',
                    isActive ? 'bg-gradient-to-l from-brand-600/30 to-transparent text-white ring-1 ring-brand-500/30'
                      : 'text-slate-400 hover:bg-white/[.05] hover:text-slate-100')
                }
              >
                <i.icon className="h-4 w-4" />
                {i.label}
              </NavLink>
            ))}
          </div>
        </aside>
        <section className="min-w-0">
          <div className="mb-6">
            <h1 className="text-xl font-extrabold text-white sm:text-2xl">{title}</h1>
            {subtitle && <p className="mt-1 text-xs leading-6 text-slate-400 sm:text-sm">{subtitle}</p>}
          </div>
          {/* ردیف بخش‌های پنل روی موبایل.
              چیپ‌ها بلندتر شدند (هدف لمسی)، آیکن گرفتند تا با یک نگاه شناخته
              شوند، و ردیف «چفت اسکرول» دارد تا بخشِ نیم‌بریده نماند. */}
          <div className="scroll-x -mx-3 mb-5 gap-2 px-3 pb-1 lg:hidden">
            {PANEL_NAV.map((i) => (
              <NavLink
                key={i.to}
                to={i.to}
                className={({ isActive }) =>
                  clsx('flex shrink-0 snap-start items-center gap-1.5 whitespace-nowrap rounded-full border px-3.5 py-2.5 text-xs transition',
                    isActive
                      ? 'border-brand-500/50 bg-brand-500/15 text-brand-200'
                      : 'border-white/10 bg-white/[.05] text-slate-400 active:bg-white/[.1]')
                }
              >
                <i.icon className="h-3.5 w-3.5" />
                {i.label}
              </NavLink>
            ))}
          </div>
          {children}
        </section>
      </div>
    </Shell>
  )
}
